import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { DollarSign, Info } from 'lucide-react';
import { toast } from 'sonner';
import AppModal from '../../common/AppModal';
import { applyInvoiceRowRules, getInvoiceBalanceDue } from '../../reports/utils/helpers';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import FinancialCloseoutInvoiceTable from './FinancialCloseoutInvoiceTable';

const prefillPaidStatus = row => {
  const next = applyInvoiceRowRules(row);
  if (getInvoiceBalanceDue(next) <= 0 && next.paymentReceived != null && next.paymentReceived !== '') {
    return { ...next, status: 'Paid' };
  }
  return next;
};

const FinancialCloseoutModal = ({
  show,
  onHide,
  showProgress,
  onSubmit,
  pendingStatus,
  orderId,
  initialValues = {},
  primaryPriceLabel = 'Hard Cost',
  requireDateDelivered = false
}) => {
  const [hardCost, setHardCost] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [dateDelivered, setDateDelivered] = useState('');

  useEffect(() => {
    if (!show) return;
    setHardCost(initialValues.hardCost ?? '');
    setDateDelivered(initialValues.dateDelivered ?? '');
  }, [show, initialValues]);

  const isPaid = pendingStatus === 'Paid';
  const isPendingToPay = pendingStatus === 'Pending To Pay';

  useEffect(() => {
    if (!show || !orderId || !isPaid) {
      setInvoices([]);
      setLoadingInvoices(false);
      return;
    }

    let cancelled = false;

    const loadInvoices = async () => {
      setLoadingInvoices(true);
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORDER_REPORT_INVOICES(orderId),
        method: 'GET'
      });
      if (cancelled) return;

      if (error) {
        toast.error(error);
        setInvoices([]);
      } else {
        setInvoices((response?.invoices || []).map(prefillPaidStatus));
      }
      setLoadingInvoices(false);
    };

    loadInvoices();

    return () => {
      cancelled = true;
    };
  }, [show, orderId, isPaid]);

  const title = isPendingToPay
    ? requireDateDelivered
      ? 'Choose a Date'
      : `${primaryPriceLabel} Required`
    : `Financial Closeout – ${isPaid ? 'Paid Service' : 'Pending To Pay'}`;

  const isHardCostInvalid = (isPaid || isPendingToPay) && hardCost === '';
  const isDateInvalid = requireDateDelivered && !dateDelivered;
  const canSubmit = !isHardCostInvalid && !isDateInvalid && !(isPaid && loadingInvoices);
  const showHardCostError = isHardCostInvalid && !isPendingToPay;
  // Date-only when hard cost already known; otherwise still collect hard cost for Pending To Pay
  const showHardCostField = isPaid || !requireDateDelivered || hardCost === '';

  const invoiceAmount = initialValues.invoiceAmount;
  const collectedFees = useMemo(() => {
    if (invoiceAmount == null) return null;
    return (Number(invoiceAmount) || 0) - (Number(hardCost) || 0);
  }, [invoiceAmount, hardCost]);

  const allInvoicesFullyPaid = useMemo(() => {
    if (!isPaid || !invoices.length) return true;
    return invoices.every(row => getInvoiceBalanceDue(row) <= 0);
  }, [isPaid, invoices]);

  const showPaidDisclaimer = isPaid && !allInvoicesFullyPaid;

  const updateInvoiceRow = (index, updater) => {
    setInvoices(prev => prev.map((row, i) => (i === index ? applyInvoiceRowRules(updater(row)) : row)));
  };

  const handleInvoiceFieldChange = (index, key, value) => {
    updateInvoiceRow(index, row => ({ ...row, [key]: value }));
  };

  const handleInvoiceStatusChange = (index, status) => {
    updateInvoiceRow(index, row => ({ ...row, status }));
  };

  const handleSubmit = () => {
    onSubmit({
      hardCost: hardCost === '' ? null : Number(hardCost),
      invoices: isPaid ? invoices : [],
      allInvoicesFullyPaid,
      dateDelivered: dateDelivered || null
    });
  };

  return (
    <AppModal
      show={show}
      onHide={onHide}
      onSubmit={handleSubmit}
      heading={
        <h6 className="mb-0 d-flex align-items-center font-weight-bold largeFont">
          {!isPendingToPay && <DollarSign size={16} aria-hidden className="mr-1 text-success" />}
          {title}
        </h6>
      }
      submitting={showProgress}
      submitDisabled={!canSubmit}
      submitLabel={isPendingToPay ? 'Submit' : 'Confirm & Save'}
      submitButtonVariant={isPendingToPay ? 'primary-new' : 'warning'}
      size={isPaid ? 'lg' : undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        {isPaid && (
          <FinancialCloseoutInvoiceTable
            invoices={invoices}
            loading={loadingInvoices}
            disabled={showProgress}
            onFieldChange={handleInvoiceFieldChange}
            onStatusChange={handleInvoiceStatusChange}
          />
        )}

        <div className="d-flex justify-content-between align-items-start" style={{ gap: 16 }}>
          {requireDateDelivered && (
            <Form.Group className="mb-0 w-100">
              <Form.Label className="font-weight-bold midFont mb-1">
                Date Delivered<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={dateDelivered}
                onChange={e => setDateDelivered(e.target.value)}
                disabled={showProgress}
                className={isDateInvalid ? 'input-invalid bg-white' : 'bg-white'}
                style={{ height: 40 }}
              />
              {isDateInvalid && <p className="xsmallFont text-rush mb-0 mt-1">Date Delivered is a required Field</p>}
            </Form.Group>
          )}

          {showHardCostField && (
            <Form.Group className="mb-0 w-100">
              <Form.Label className="font-weight-bold midFont mb-1">
                {primaryPriceLabel} ($){isPaid && <span className="text-danger"> *</span>}
              </Form.Label>
              <Form.Control
                type="number"
                placeholder=""
                value={hardCost}
                onChange={e => setHardCost(e.target.value)}
                disabled={showProgress}
                className={showHardCostError ? 'input-invalid bg-white' : 'bg-white'}
                style={{ height: 40 }}
              />
              {showHardCostError && (
                <p className="xsmallFont text-rush mb-0 mt-1">{primaryPriceLabel} is a required Field</p>
              )}
            </Form.Group>
          )}

          {isPaid && (
            <Form.Group className="mb-0 w-100">
              <Form.Label className="font-weight-bold midFont mb-1">Collected Fees ($)</Form.Label>
              <Form.Control
                type="text"
                value={collectedFees != null ? collectedFees.toFixed(2) : 'N/A'}
                disabled
                readOnly
                className="bg-white border-0 font-weight-bold text-dark"
                style={{ height: 40 }}
              />
            </Form.Group>
          )}
        </div>

        {showPaidDisclaimer && (
          <p
            className="d-flex align-items-start midFont mb-0 p-2 rounded-lg"
            style={{
              gap: 8,
              border: '1px solid #ffc10766',
              backgroundColor: '#ffc10726',
              color: '#856404'
            }}
          >
            <Info size={16} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              Order status will not change to Paid while one or more invoices have a balance due. Invoice and hard cost
              changes will still be saved.
            </span>
          </p>
        )}
      </div>
    </AppModal>
  );
};

export default FinancialCloseoutModal;
